/**
 * Zustand store for the Configuration Page
 */

import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

/**
 * Joint information extracted from URDF
 */
export interface JointInfo {
  name: string;
  type: string;
  parentLink: string;
  childLink: string;
  axis: [number, number, number];
  lowerLimit: number;
  upperLimit: number;
  velocityLimit: number;
  effortLimit: number;
}

/**
 * Servo mapping configuration for a joint
 */
export interface ServoMapping {
  jointName: string;
  servoId: number | null;
  direction: 1 | -1;
  angleOffset: number;
  minAngle: number;
  maxAngle: number;
}

/**
 * MoveIt planning group definition
 */
export interface PlanningGroup {
  name: string;
  joints: string[];
  endEffectorLink: string | null;
  baseLink: string | null;
}

/**
 * MoveIt controller configuration
 */
export interface ControllerConfig {
  name: string;
  type: "joint_trajectory_controller" | "gripper_controller" | "position_controller";
  joints: string[];
}

/**
 * Gazebo plugin configuration
 */
export interface GazeboPlugin {
  name: string;
  filename: string;
  enabled: boolean;
  parameters: Record<string, string | number | boolean>;
}

/**
 * Gazebo physics configuration
 */
export interface GazeboPhysics {
  solver: "ode" | "bullet" | "simbody" | "dart";
  stepSize: number;
  realTimeFactor: number;
}

/**
 * Imitation Learning observation configuration
 */
export interface ILObservationConfig {
  includeJointPositions: boolean;
  includeJointVelocities: boolean;
  includeEndEffectorPose: boolean;
  customObservations: string[];
}

/**
 * Imitation Learning action configuration
 */
export interface ILActionConfig {
  actionType: "joint_positions" | "joint_velocities" | "end_effector_delta";
  selectedJoints: string[];
  actionScale: number;
}

/**
 * Link information extracted from URDF
 */
export interface LinkInfo {
  name: string;
  hasVisual: boolean;
  hasCollision: boolean;
  hasInertial: boolean;
}

/**
 * Export options - which files to generate
 */
export interface ExportOptions {
  includeUrdf: boolean;
  includeServoMapping: boolean;
  includeMoveIt: boolean;
  includeGazebo: boolean;
  includeImitationLearning: boolean;
}

/**
 * Configuration store state
 */
interface ConfigureState {
  // Step 1: URDF Input
  urdfContent: string;
  urdfFileName: string | null;
  packageName: string;
  isUrdfParsed: boolean;
  parseError: string | null;

  // Parsed URDF data
  joints: JointInfo[];
  links: LinkInfo[];
  rootLink: string | null;

  // 3D Viewer state (highlighting is managed by Builder's store)
  highlightedJoint: string | null;

  // Step 2: Servo Mapping
  servoMappings: ServoMapping[];

  // Step 3: MoveIt2
  planningGroups: PlanningGroup[];
  controllers: ControllerConfig[];
  disabledCollisionPairs: Array<[string, string]>;

  // Step 4: Gazebo
  gazeboPhysics: GazeboPhysics;
  gazeboPlugins: GazeboPlugin[];

  // Step 5: Imitation Learning
  ilObservation: ILObservationConfig;
  ilAction: ILActionConfig;

  // Export options
  exportOptions: ExportOptions;

  // Actions
  setPackageName: (name: string) => void;
  setUrdfContent: (content: string, fileName?: string) => void;
  setUrdfParsed: (
    joints: JointInfo[],
    links: LinkInfo[],
    rootLink: string | null
  ) => void;
  setParseError: (error: string | null) => void;

  // Servo mapping actions
  updateServoMapping: (jointName: string, mapping: Partial<ServoMapping>) => void;

  // MoveIt actions
  addPlanningGroup: (group: PlanningGroup) => void;
  updatePlanningGroup: (index: number, group: Partial<PlanningGroup>) => void;
  removePlanningGroup: (index: number) => void;
  addController: (controller: ControllerConfig) => void;
  updateController: (index: number, controller: Partial<ControllerConfig>) => void;
  removeController: (index: number) => void;
  addDisabledCollisionPair: (link1: string, link2: string) => void;
  removeDisabledCollisionPair: (index: number) => void;

  // Gazebo actions
  updateGazeboPhysics: (physics: Partial<GazeboPhysics>) => void;
  toggleGazeboPlugin: (pluginName: string) => void;
  updateGazeboPlugin: (pluginName: string, params: Record<string, string | number | boolean>) => void;

  // IL actions
  updateILObservation: (config: Partial<ILObservationConfig>) => void;
  updateILAction: (config: Partial<ILActionConfig>) => void;

  // Export options actions
  updateExportOptions: (options: Partial<ExportOptions>) => void;

  // 3D Viewer actions
  setHighlightedJoint: (jointName: string | null) => void;

  // Reset
  reset: () => void;
}

const defaultGazeboPlugins: GazeboPlugin[] = [
  {
    name: "ros2_control",
    filename: "libgazebo_ros2_control.so",
    enabled: true,
    parameters: {},
  },
  {
    name: "joint_state_publisher",
    filename: "libgazebo_ros_joint_state_publisher.so",
    enabled: true,
    parameters: {
      update_rate: 100,
    },
  },
];

const initialState = {
  urdfContent: "",
  urdfFileName: null,
  packageName: "assembled_robot",
  isUrdfParsed: false,
  parseError: null,
  joints: [],
  links: [],
  rootLink: null,
  highlightedJoint: null,
  servoMappings: [],
  planningGroups: [],
  controllers: [],
  disabledCollisionPairs: [],
  gazeboPhysics: {
    solver: "ode" as const,
    stepSize: 0.001,
    realTimeFactor: 1.0,
  },
  gazeboPlugins: defaultGazeboPlugins,
  ilObservation: {
    includeJointPositions: true,
    includeJointVelocities: true,
    includeEndEffectorPose: false,
    customObservations: [],
  },
  ilAction: {
    actionType: "joint_positions" as const,
    selectedJoints: [],
    actionScale: 1.0,
  },
  exportOptions: {
    includeUrdf: true,
    includeServoMapping: true,
    includeMoveIt: true,
    includeGazebo: true,
    includeImitationLearning: true,
  },
};

export const useConfigureStore = create<ConfigureState>()(
  immer((set) => ({
    ...initialState,

    setPackageName: (name) =>
      set((state) => {
        state.packageName = name;
      }),

    setUrdfContent: (content, fileName) =>
      set((state) => {
        state.urdfContent = content;
        state.urdfFileName = fileName ?? null;
        state.isUrdfParsed = false;
        state.parseError = null;
      }),

    setUrdfParsed: (joints, links, rootLink) =>
      set((state) => {
        state.joints = joints;
        state.links = links;
        state.rootLink = rootLink;
        state.isUrdfParsed = true;
        state.parseError = null;

        // Initialize servo mappings for each joint with incrementing servo IDs
        state.servoMappings = joints
          .filter((j) => j.type !== "fixed")
          .map((joint, index) => ({
            jointName: joint.name,
            servoId: index,
            direction: 1,
            angleOffset: 0,
            minAngle: joint.lowerLimit,
            maxAngle: joint.upperLimit,
          }));

        // Initialize IL action with all non-fixed joints
        state.ilAction.selectedJoints = joints
          .filter((j) => j.type !== "fixed")
          .map((j) => j.name);
      }),

    setParseError: (error) =>
      set((state) => {
        state.parseError = error;
        state.isUrdfParsed = false;
      }),

    updateServoMapping: (jointName, mapping) =>
      set((state) => {
        const index = state.servoMappings.findIndex(
          (m) => m.jointName === jointName
        );
        if (index !== -1) {
          Object.assign(state.servoMappings[index], mapping);
        }
      }),

    addPlanningGroup: (group) =>
      set((state) => {
        state.planningGroups.push(group);
      }),

    updatePlanningGroup: (index, group) =>
      set((state) => {
        if (index >= 0 && index < state.planningGroups.length) {
          Object.assign(state.planningGroups[index], group);
        }
      }),

    removePlanningGroup: (index) =>
      set((state) => {
        state.planningGroups.splice(index, 1);
      }),

    addController: (controller) =>
      set((state) => {
        state.controllers.push(controller);
      }),

    updateController: (index, controller) =>
      set((state) => {
        if (index >= 0 && index < state.controllers.length) {
          Object.assign(state.controllers[index], controller);
        }
      }),

    removeController: (index) =>
      set((state) => {
        state.controllers.splice(index, 1);
      }),

    addDisabledCollisionPair: (link1, link2) =>
      set((state) => {
        state.disabledCollisionPairs.push([link1, link2]);
      }),

    removeDisabledCollisionPair: (index) =>
      set((state) => {
        state.disabledCollisionPairs.splice(index, 1);
      }),

    updateGazeboPhysics: (physics) =>
      set((state) => {
        Object.assign(state.gazeboPhysics, physics);
      }),

    toggleGazeboPlugin: (pluginName) =>
      set((state) => {
        const plugin = state.gazeboPlugins.find((p) => p.name === pluginName);
        if (plugin) {
          plugin.enabled = !plugin.enabled;
        }
      }),

    updateGazeboPlugin: (pluginName, params) =>
      set((state) => {
        const plugin = state.gazeboPlugins.find((p) => p.name === pluginName);
        if (plugin) {
          Object.assign(plugin.parameters, params);
        }
      }),

    updateILObservation: (config) =>
      set((state) => {
        Object.assign(state.ilObservation, config);
      }),

    updateILAction: (config) =>
      set((state) => {
        Object.assign(state.ilAction, config);
      }),

    updateExportOptions: (options) =>
      set((state) => {
        Object.assign(state.exportOptions, options);
      }),

    setHighlightedJoint: (jointName) =>
      set((state) => {
        state.highlightedJoint = jointName;
      }),

    reset: () =>
      set(() => ({
        ...initialState,
        gazeboPlugins: [...defaultGazeboPlugins],
      })),
  }))
);
